/**
 * CommandModal.jsx - Premium Command Modal
 *
 * Modal para envio rápido de comandos diretamente do mapa/lista
 * - Pre-seleciona comando baseado no status de bloqueio
 * - Se blocked: true → seleciona "engineResume" (Religar Motor)
 * - Se blocked: false → seleciona "engineStop" (Desligar Motor)
 * - Responsivo para mobile
 */

import {
 useState, useEffect, useCallback, memo 
} from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { PremiumModal, ModalFooter } from '../../common/components/premium/PremiumModal';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { prefixString } from '../../common/util/stringUtils';

// Comandos comuns que queremos destacar
const PRIORITY_COMMANDS = ['engineStop', 'engineResume', 'positionPeriodic', 'custom'];

const CommandModal = memo(({
  open,
  onClose,
  deviceId,
  deviceName,
  blocked = false,
  onSuccess,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [commands, setCommands] = useState([]);
  const [selectedCommand, setSelectedCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load available commands when modal opens
  useEffect(() => {
    if (!open || !deviceId) return;

    const loadCommands = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/commands/types?deviceId=${deviceId}`);
        if (!response.ok) throw new Error('Failed to load commands');
        const types = await response.json();

        // Sort: priority commands first
        const sorted = types.sort((a, b) => {
          const aPriority = PRIORITY_COMMANDS.indexOf(a.type);
          const bPriority = PRIORITY_COMMANDS.indexOf(b.type);
          if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
          if (aPriority !== -1) return -1;
          if (bPriority !== -1) return 1;
          return 0;
        });

        setCommands(sorted);

        // Pre-select based on blocked status
        const defaultCommand = blocked ? 'engineResume' : 'engineStop';
        const hasDefault = sorted.some(cmd => cmd.type === defaultCommand);

        if (hasDefault) {
          setSelectedCommand(defaultCommand);
        } else if (sorted.length > 0) {
          setSelectedCommand(sorted[0].type);
        }
      } catch (err) {
        setError(err.message || 'Erro ao carregar comandos');
      } finally {
        setLoading(false);
      }
    };

    loadCommands();
  }, [open, deviceId, blocked]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setSuccess(false);
      setSending(false);
    }
  }, [open]);

  // Send command
  const handleSend = useCallback(async () => {
    if (!selectedCommand || !deviceId) return;

    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/commands/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: parseInt(deviceId, 10),
          type: selectedCommand,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Falha ao enviar comando');
      }

      setSuccess(true);
      onSuccess?.();

      // Close after success
      setTimeout(() => {
        onClose?.();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao enviar comando');
    } finally {
      setSending(false);
    }
  }, [selectedCommand, deviceId, onSuccess, onClose]);

  // Get command icon
  const getCommandIcon = (type) => {
    switch (type) {
      case 'engineStop':
        return <LockIcon sx={{ color: tokens?.status?.alarm || '#ef4444' }} />;
      case 'engineResume':
        return <LockOpenIcon sx={{ color: tokens?.status?.moving || '#22c55e' }} />;
      default:
        return <SettingsIcon sx={{ color: tokens?.text?.secondary || '#9ca3af' }} />;
    }
  };

  // Get command color hint
  const getCommandColor = (type) => {
    switch (type) {
      case 'engineStop':
        return tokens?.status?.alarm || '#ef4444';
      case 'engineResume':
        return tokens?.status?.moving || '#22c55e';
      default:
        return tokens?.accent?.primary || '#14b8a6';
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title={t('commandSend') || 'Enviar Comando'}
      subtitle={deviceName || `Device ${deviceId}`}
      icon={<SendIcon />}
      size={isMobile ? 'full' : 'sm'}
      fullScreen={isMobile}
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSend}
          cancelLabel={t('sharedCancel') || 'Cancelar'}
          confirmLabel={sending ? (t('sharedSending') || 'Enviando...') : (t('commandSend') || 'Enviar')}
          confirmLoading={sending}
          confirmDisabled={!selectedCommand || loading || success}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Status hint */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: tokens?.radius?.md || 8,
            bgcolor: alpha(blocked ? (tokens?.status?.alarm || '#ef4444') : (tokens?.status?.moving || '#22c55e'), 0.1),
            border: `1px solid ${alpha(blocked ? (tokens?.status?.alarm || '#ef4444') : (tokens?.status?.moving || '#22c55e'), 0.3)}`,
          }}
        >
          {blocked ? (
            <LockIcon sx={{ color: tokens?.status?.alarm || '#ef4444' }} />
          ) : (
            <LockOpenIcon sx={{ color: tokens?.status?.moving || '#22c55e' }} />
          )}
          <Typography
            sx={{
              fontSize: 14,
              color: blocked ? (tokens?.status?.alarm || '#ef4444') : (tokens?.status?.moving || '#22c55e'),
              fontWeight: 500,
            }}
          >
            {blocked
              ? (t('deviceBlocked') || 'Motor Bloqueado')
              : (t('deviceUnblocked') || 'Motor Desbloqueado')}
          </Typography>
        </Box>

        {/* Command selector */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: tokens?.accent?.primary || '#14b8a6' }} />
          </Box>
        ) : (
          <FormControl fullWidth onClick={(e) => e.stopPropagation()}>
            <InputLabel
              id="command-select-label"
              sx={{ color: tokens?.text?.secondary || '#9ca3af' }}
            >
              {t('sharedType') || 'Tipo de Comando'}
            </InputLabel>
            <Select
              labelId="command-select-label"
              value={selectedCommand}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedCommand(e.target.value);
              }}
              label={t('sharedType') || 'Tipo de Comando'}
              sx={{
                bgcolor: tokens?.surface?.input || 'rgba(255,255,255,0.05)',
                borderRadius: tokens?.radius?.md || 8,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: tokens?.border?.default || 'rgba(255,255,255,0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: tokens?.border?.hover || 'rgba(255,255,255,0.2)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: getCommandColor(selectedCommand),
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                },
              }}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: {
                    bgcolor: tokens?.surface?.elevated || '#1f2937',
                    border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.1)'}`,
                    borderRadius: tokens?.radius?.md || 8,
                    maxHeight: 300,
                  },
                },
              }}
            >
              {commands.map((cmd) => (
                <MenuItem
                  key={cmd.type}
                  value={cmd.type}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: alpha(getCommandColor(cmd.type), 0.1),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(getCommandColor(cmd.type), 0.15),
                      '&:hover': {
                        bgcolor: alpha(getCommandColor(cmd.type), 0.2),
                      },
                    },
                  }}
                >
                  {getCommandIcon(cmd.type)}
                  <Typography sx={{ color: tokens?.text?.primary || '#f9fafb' }}>
                    {t(prefixString('command', cmd.type)) || cmd.type}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              bgcolor: alpha(tokens?.status?.alarm || '#ef4444', 0.1),
              color: tokens?.status?.alarm || '#ef4444',
              borderRadius: tokens?.radius?.md || 8,
              '& .MuiAlert-icon': {
                color: tokens?.status?.alarm || '#ef4444',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Success message */}
        {success && (
          <Alert
            severity="success"
            sx={{
              bgcolor: alpha(tokens?.status?.moving || '#22c55e', 0.1),
              color: tokens?.status?.moving || '#22c55e',
              borderRadius: tokens?.radius?.md || 8,
              '& .MuiAlert-icon': {
                color: tokens?.status?.moving || '#22c55e',
              },
            }}
          >
            {t('commandSent') || 'Comando enviado com sucesso!'}
          </Alert>
        )}

        {/* Quick action hint */}
        {!loading && !success && selectedCommand && (
          <Typography
            sx={{
              fontSize: 12,
              color: tokens?.text?.muted || '#6b7280',
              textAlign: 'center',
              mt: 1,
            }}
          >
            {selectedCommand === 'engineStop'
              ? (t('commandEngineStopHint') || 'Este comando irá bloquear o motor do veículo')
              : selectedCommand === 'engineResume'
                ? (t('commandEngineResumeHint') || 'Este comando irá desbloquear o motor do veículo')
                : (t('commandGenericHint') || 'Certifique-se de que o comando é compatível com o dispositivo')}
          </Typography>
        )}
      </Box>
    </PremiumModal>
  );
});

CommandModal.displayName = 'CommandModal';
export default CommandModal;
